import { useState, useEffect } from 'react';

import { getQuestions, getUserData, getStoredAnswers, saveAnswers } from '../functions/api';
import { loadFromLocalStorage, saveToLocalStorage, removeFromLocalStorage } from '../functions/local';

import './App.css';
import DSGProgressBar from './general/DSGProgressBar';
import DSGNavigation from './general/DSGNavigation';
import Questionnaire from './general/Questionnaire';

import FirstPage from './general/FirstPage';
import BlockedPage from './general/BlockedPage';
import OutputPage from './output/OutputPage';
import TermsPage from './output/TermsPage';

import 'react-confirm-alert/src/react-confirm-alert.css';

import { LOCAL_STORAGE_AGREEMENT_KEY } from './output/TermsPage';

const LOCAL_STORAGE_ANSWERS_KEY = 'lexdex.answers';
const LOCAL_STORAGE_PAGE_KEY = 'lexdex.page';

const isStoreEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === 'object' && Object.keys(value).length === 0) return true;
  return false;
};

const loadSpecificStore = (key, userId) => {
  if(!userId) {
    const value = loadFromLocalStorage(key);
    console.log('Loading from generic store:', key, value);
    return value;
  } else {
    var genericStore = loadFromLocalStorage(key);
    var specificStore = loadFromLocalStorage(key + "." + userId);
    console.log('Loading stores for user', userId, '- Generic:', genericStore, 'Specific:', specificStore);
    
    if(isStoreEmpty(specificStore)) {
      if(!isStoreEmpty(genericStore)) {
        console.log('Using generic store for user', userId);
        saveSpecificStore(key + "." + userId, genericStore, userId);
        saveSpecificStore(key, {}, userId);
      }
      return genericStore;
    } else {
      console.log('Using specific store for user', userId);
      return specificStore;
    }
  }
}

const saveSpecificStore = (key, value, userId) => {
  console.log('Saving to store:', key, value, 'for user:', userId);
  if(!userId) {
    return saveToLocalStorage(key, value);
  } else {
    return saveToLocalStorage(key + "." + userId, value);
  }
}

function App() {
  const [info, setInfo] = useState({});
  const [questions, setQuestions] = useState([]);
  const [currentPage, setCurrentPageState] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [answers, setAnswers] = useState(null);
  const [alert, setAlert] = useState(null);
  const [marked, setMarked] = useState([]);

  const [isEligible, setIsEligible] = useState(false);
  const [usageCount, setUsageCount] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [availableUses, setAvailableUses] = useState(0);
  const [hasStoredResults, setHasStoredResults] = useState(false);
  const [userId, setUserId] = useState(undefined);
  
  const setCurrentPage = (page) => {
    console.log('Setting current page to:', page);
    setCurrentPageState(page);
    saveSpecificStore(LOCAL_STORAGE_PAGE_KEY, page, userId);
  }

  // Load user data first
  useEffect(() => {
    console.log('Initial user data load effect');
    updateData();
  }, []);

  const updateData = async () => {
    console.log('Updating user data...');
    try {
      const data = await getUserData();
      console.log('Received user data:', data);
      
      if (!data) {
        console.log('No user data received');
        return;
      }

      setIsEligible(data.is_eligible);
      setUsageCount(data.usage_count);
      const newIsLoggedIn = data.user_id && data.user_id !== 0;
      setIsLoggedIn(newIsLoggedIn);
      setUserId(data.user_id);
      setAvailableUses(data.available_usage_count);
      setHasStoredResults(data.has_stored_results);

      // Load page from storage after user data is set
      const storedPage = loadSpecificStore(LOCAL_STORAGE_PAGE_KEY, data.user_id);
      if (storedPage !== null) {
        console.log('Setting page from storage:', storedPage);
        setCurrentPage(parseInt(storedPage) || 0);
      }
    } catch (error) {
      console.error('Error updating user data:', error);
    }
  };

  // Load answers after user data is set
  useEffect(() => {
    console.log('Answers load effect. isLoggedIn:', isLoggedIn, 'userId:', userId);
    
    const loadAnswers = async () => {
      if (isLoggedIn) {
        console.log('User is logged in, attempting to load answers from DB');
        try {
          const response = await getStoredAnswers();
          console.log('DB answers response:', response);
          
          if (response && Object.keys(response).length > 0) {
            console.log('Setting answers from DB:', response);
            setAnswers(response);
            // Also save to localStorage as backup
            saveToLocalStorage(LOCAL_STORAGE_ANSWERS_KEY, response);
          } else {
            console.log('No answers in DB, checking localStorage');
            const localAnswers = loadFromLocalStorage(LOCAL_STORAGE_ANSWERS_KEY);
            if (localAnswers && Object.keys(localAnswers).length > 0) {
              console.log('Setting answers from localStorage:', localAnswers);
              setAnswers(localAnswers);
            } else {
              console.log('No answers found in localStorage');
              setAnswers({});
            }
          }
        } catch (error) {
          console.error('Error loading answers from DB:', error);
          console.log('Falling back to localStorage');
          const localAnswers = loadFromLocalStorage(LOCAL_STORAGE_ANSWERS_KEY);
          if (localAnswers) {
            console.log('Setting answers from localStorage fallback:', localAnswers);
            setAnswers(localAnswers);
          }
        }
      } else {
        console.log('User not logged in, using localStorage only');
        const localAnswers = loadFromLocalStorage(LOCAL_STORAGE_ANSWERS_KEY);
        if (localAnswers) {
          console.log('Setting answers from localStorage (not logged in):', localAnswers);
          setAnswers(localAnswers);
        }
      }
    };

    loadAnswers();
  }, [isLoggedIn, userId]);

  // Save changes - only save to DB when form is complete
  useEffect(() => {
    if (!answers) {
      console.log('No answers to save');
      return;
    }
    
    console.log('Saving answers to localStorage');
    saveToLocalStorage(LOCAL_STORAGE_ANSWERS_KEY, answers);
    
    if (isLoggedIn && currentPage === totalPages + 2) {
      console.log('Form complete, saving answers to DB:', answers);
      saveAnswers(answers).catch(err => {
        console.error('Failed to save answers to DB:', err);
      });
    }
  }, [answers, isLoggedIn, currentPage, totalPages]);

  // initialization effects
  useEffect(() => {
    console.log('Initializing questions');
    setAlert(null);

    getQuestions().then(data => {
      console.log('Received questions data:', data);
      setInfo(data.info);
      setQuestions(data.questions)

      let highestPageNumber = 0;
      data.questions.forEach(question => {
        if (question.page > highestPageNumber) {
          highestPageNumber = question.page;
        }
      });
      console.log('Setting total pages to:', highestPageNumber);
      setTotalPages(highestPageNumber);
      
      if (!currentPage || currentPage > highestPageNumber + 2 || currentPage < 0 || isNaN(currentPage)) {
        console.log('Resetting current page to 0');
        setCurrentPage(0);
      }
    })
  }, []);

  const handleAnswerChange = (id, answer) => {
    console.log('Handling answer change:', id, answer);
    setAnswers(prevAnswers => ({ ...prevAnswers, [id]: answer }));

    if (marked.includes(id)) {
      setMarked(prevMarked => prevMarked.filter(questionId => questionId !== id));
    }

    const currentQuestion = questions.find(question => question.id === id);
    if (!currentQuestion.pid) {
      return;
    }

    const parentQuestion = questions.find(question => question.id === currentQuestion.pid);
    if (parentQuestion && (parentQuestion.type === 'select' || parentQuestion.type === 'group')) {
      const childQuestions = questions.filter(question => question.pid === parentQuestion.id);
      const positiveAnswers = childQuestions.filter(question => {
        var qAnswer = question.id === id ? answer : answers[question.id];
        return question.type === 'yesno' ? (qAnswer === true || qAnswer === 'yes') :
          question.type === 'insert' ? qAnswer && qAnswer.length > 0 :
            false;
      }).length;

      const parentAnswer = positiveAnswers > 0;
      setAnswers(prevAnswers => ({ ...prevAnswers, [id]: answer, [parentQuestion.id]: parentAnswer }));
    }
  };

  const getQuestionsOnPage = (page) => {
    return questions.filter(question => question.page === page).sort((a, b) => a.id - b.id);
  }

  const currentQuestions = getQuestionsOnPage(currentPage);

  const validateAnswers = (page) => {
    console.log("Validating answers for page:", page);
    var unansweredQuestions = [];
    var checkedQuestions = [];
    var validQuestions = [];

    var consideredQuestions = currentQuestions;
    if (page) {
      consideredQuestions = getQuestionsOnPage(page);
    }

    consideredQuestions.forEach(question => {
      if ((question.pid && consideredQuestions.find(q => q.id === question.pid && q.type === 'select')) ||
        (question.pid && answers[question.pid] === false)) {
        return;
      }

      checkedQuestions.push(question.id);

      if (question.type === 'insert' || question.optional) {
        validQuestions.push(question.id);
        return;
      }

      if (question.type === 'group' || question.type === 'select') {
        if (question.min && answers[question.id] !== true) {
          unansweredQuestions.push(question.id);
        } else {
          validQuestions.push(question.id);
        }
      } else if (answers[question.id] === undefined || answers[question.id] === null || answers[question.id] === "") {
        unansweredQuestions.push(question.id);
      } else {
        validQuestions.push(question.id);
      }
    });

    setMarked(unansweredQuestions);
    const allQuestionsAnswered = unansweredQuestions.length === 0;
    console.log('Validation result:', { allQuestionsAnswered, unansweredQuestions, validQuestions });
    return allQuestionsAnswered;
  }

  const navigateToPage = (pageNumber) => {
    console.log('Navigating to page:', pageNumber);
    setAlert(null);

    if (pageNumber < 0 || pageNumber > totalPages + 2) {
      console.log(`Invalid page number ${pageNumber}. Doing nothing.`);
      setAlert("Ungültige Anfrage.");
      return;
    }

    var navToPreviousPage = pageNumber < currentPage;
    if (navToPreviousPage) {
      setCurrentPage(pageNumber);
      return;
    }

    var navToComingPage = pageNumber > currentPage;
    if (navToComingPage) {
      for (var i = currentPage; i < pageNumber; i++) {
        var allQuestionsAnswered = validateAnswers(i);
        if (!allQuestionsAnswered) {
          setAlert("Bitte beantworten Sie alle Pflichtfragen auf Seite " + (i) + ".");
          console.log("Not all questions answered on page " + (i) + ". Staying on page " + currentPage);
          setCurrentPage(i);
          return;
        } else {
          setAlert(null);
        }
      }
    }

    allQuestionsAnswered = validateAnswers();

    if (!allQuestionsAnswered) {
      setAlert("Bitte beantworten Sie alle Pflichtfragen.");
      return;
    } else {
      setAlert(null);
    }

    if (pageNumber === totalPages + 2) {
      if (loadSpecificStore(LOCAL_STORAGE_AGREEMENT_KEY, userId) === false) {
        navigateToPage(totalPages + 1);
        setAlert("Bitte akzeptieren Sie die Nutzungsbedingungen.");
        return;
      }
    }

    setCurrentPage(pageNumber);
  }

  const clearAnswers = () => {
    console.log('Clearing answers');
    removeFromLocalStorage(LOCAL_STORAGE_ANSWERS_KEY);
    setAnswers({});
  }

  const isFirstPage = currentPage === 0;
  const isSecondToLastPage = currentPage === totalPages + 1;
  const isLastPage = currentPage === totalPages + 2;

  return (
    <div className="App">
      <header className="App-header">
        <DSGProgressBar totalPages={totalPages} currentPage={currentPage} navigateToPage={navigateToPage} />
        <hr></hr>
        <DSGNavigation currentPage={currentPage} navigateToPage={navigateToPage} totalPages={totalPages} alert={alert} top={true} />
      </header>
      <main className="App-main">
        {
          isFirstPage
            ?
            <FirstPage clearAnswers={clearAnswers} setAnswers={setAnswers} intro={info.intro}
              isEligible={isEligible} isLoggedIn={isLoggedIn} usageCount={usageCount} availableUses={availableUses} />
            :
            isSecondToLastPage
              ?
              <TermsPage terms={info.terms} answers={answers} updateData={updateData} hasStoredResults={hasStoredResults} />
              :
              isLastPage
                ?
                <OutputPage outro={info.outro} answers={answers} updateData={updateData} hasStoredResults={hasStoredResults}
                  isEligible={isEligible} isLoggedIn={isLoggedIn} usageCount={usageCount} availableUses={availableUses} />
                :
                ""
        }
        {
          !isEligible && currentPage >= 6
            ?
            <BlockedPage isEligible={isEligible} isLoggedIn={isLoggedIn} usageCount={usageCount} availableUses={availableUses} />
            :
            <Questionnaire key={currentPage} info={info} questions={currentQuestions} answers={answers} marked={marked} handleAnswerChange={handleAnswerChange} />
        }
      </main>
      <footer className="App-footer">
        <DSGNavigation currentPage={currentPage} navigateToPage={navigateToPage} totalPages={totalPages} alert={alert} />
      </footer>
    </div>
  );
}

export default App;
